import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Typography } from '@la/ds-ui-components';
import { FileUploadFormField } from '@la/types';
import {
  formatAsUSD,
  getProductPropertyLabel,
  isStaffRegistration,
} from '@la/utilities';
import {
  RegistrantInfoPayload,
  useRegistration,
} from 'lib/context/RegistrationContext/RegistrationContext';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { ProductWithMappedSelections } from '../MemberRegistration.types';
import { RegistrationInfoCard } from '../RegistrationInfoCard/RegistrationInfoCard';
import styles from './RegistrationReviewCard.module.scss';
import * as S from './RegistrationReviewCard.styles';

export const RegistrationReviewCard = () => {
  const [firstRender, setFirstRender] = useState(true);
  const {
    ctx,
    fileUploadFormFields,
    formFields,
    ngbMembershipType,
    setHasErrors,
    showNGBVerification,
    signedWaivers,
    waivers,
  } = useRegistration();

  const { getSelectedProducts } = useRegistrationAddOns();

  const selectedProducts = getSelectedProducts();

  useEffect(() => {
    if (firstRender) {
      setHasErrors(false);
      setFirstRender(false);
    }
  }, [firstRender, setHasErrors]);

  const renderFieldsCard = () => {
    // We will always show the info card.
    // If it is a staff registration and there is no fields we show the user's name.
    // In case of a player registration with no fields we show the player's name.
    const allowEdit =
      !isStaffRegistration(ctx?.type) ||
      (formFields && formFields.nonFileUploadFormFields?.length > 0) ||
      (showNGBVerification && ngbMembershipType);

    return <RegistrationInfoCard allowEditMode={Boolean(allowEdit)} />;
  };

  const renderUploadDocumentsCard = () => {
    const filenames = Object.values(fileUploadFormFields);

    if (!filenames.length) {
      return null;
    }

    return (
      <Card>
        <CardHeader>Uploaded Documents</CardHeader>
        <CardBody>
          <Files fileUploadFormFields={filenames} />
        </CardBody>
      </Card>
    );
  };

  return (
    <div className={styles.registrationReviewContainer}>
      {renderFieldsCard()}
      {renderUploadDocumentsCard()}
      <Card>
        <CardHeader>Signed Documents</CardHeader>
        <CardBody>
          <div className={styles.entriesContainer}>
            <Waivers waivers={waivers} signedWaivers={signedWaivers} />
          </div>
        </CardBody>
      </Card>
      {selectedProducts.length > 0 ? (
        <AddOnSelectionsCard selectedProducts={selectedProducts} />
      ) : null}
    </div>
  );
};

const Files = ({
  fileUploadFormFields,
}: {
  fileUploadFormFields: FileUploadFormField[];
}) => {
  return (
    <>
      {fileUploadFormFields.map((fileUploadFormField, i) => {
        return (
          <div className={styles.waiversContainer} key={i}>
            <Typography variant="ui" size="large">
              {fileUploadFormField.name}:
            </Typography>
            <Typography variant="ui" size="large" weight="bold">
              {fileUploadFormField.value?.name}
            </Typography>
          </div>
        );
      })}
    </>
  );
};

const Waivers = ({
  waivers,
  signedWaivers,
}: {
  waivers: RegistrantInfoPayload['waivers'] | null;
  signedWaivers: Record<string, boolean>;
}) => {
  if (!waivers?.length) {
    return null;
  }

  return (
    <>
      {waivers.map((waiver, i) => {
        const isSigned = signedWaivers[waiver.waiverId];

        return (
          <div className={styles.waiversContainer} key={waiver.waiverId ?? i}>
            <Typography variant="ui" size="large">
              {waiver.name}:
            </Typography>
            <Typography variant="ui" size="large" weight="bold">
              {isSigned ? 'Signed' : 'Not signed'}
            </Typography>
          </div>
        );
      })}
    </>
  );
};

export function AddOnSelectionsCard({
  selectedProducts,
}: Readonly<{
  selectedProducts: ProductWithMappedSelections[];
}>) {
  return (
    <Card>
      <CardHeader>Selected Add-ons</CardHeader>
      <CardBody>
        <div className={styles.entriesContainer}>
          <AddOnSelectionEntries selectedProducts={selectedProducts} />
        </div>
      </CardBody>
    </Card>
  );
}

export function AddOnSelectionEntries({
  selectedProducts,
}: Readonly<{ selectedProducts: ProductWithMappedSelections[] }>) {
  const { getAddOnProductById } = useRegistrationAddOns();

  return (
    <>
      {selectedProducts.map(
        (
          {
            addOnType,
            productId,
            productName,
            quantity,
            salesTax,
            selectionTypes,
            subTotal,
          },
          idx
        ) => {
          const priceDisplay = calculateDisplayPrice(
            addOnType,
            salesTax,
            subTotal
          );
          const rawProductData = getAddOnProductById(productId);
          let propertiesDescription = `Quantity: ${quantity}`;

          for (const selection of selectionTypes) {
            const { optionId, selectedValue } = selection;
            const property = rawProductData?.properties?.find(
              (property) => property.id === Number(optionId)
            );

            if (property) {
              const value = getProductPropertyLabel(selectedValue, property);

              propertiesDescription = propertiesDescription.concat(
                `, ${property.name}: ${value}`
              );
            }
          }

          return (
            <div className={styles.addOnEntry} key={`${productId}-${idx}`}>
              <div className={styles.fieldEntry}>
                <Typography variant="ui" size="large">
                  {productName}
                </Typography>
                <Typography variant="ui" size="large" weight="bold">
                  {priceDisplay}
                </Typography>
              </div>
              <S.AddOnPropertiesDesc>
                {propertiesDescription}
              </S.AddOnPropertiesDesc>
            </div>
          );
        }
      )}
    </>
  );
}

export function calculateDisplayPrice(
  addOnType: 'optional' | 'required',
  salesTax: number,
  subTotal: number
): string {
  if (subTotal === 0) {
    return addOnType === 'required' ? 'Included' : 'Free';
  }

  return formatAsUSD(subTotal + salesTax);
}
